'use client';

import { c as _c } from "react/compiler-runtime";
import * as ChannelService from '@channel.io/channel-web-sdk-loader';
import { Button, Checkbox, DropdownMenu, Input, InputDecorator } from '@common/components';
import { useToast } from '@common/hooks';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { FileUploadPlusBox } from '@/app/_components/FileUploadPlusBox';
import { INQUIRY_TYPE } from '@/app/donation/constants';
import type { InquiryType } from '@/app/donation/types';
import { Image, LoadingPageTemplate } from '@/components/client';
import type { InquiryFormParams } from './_hooks/useInquiryForm';
import { useInquiryForm } from './_hooks/useInquiryForm';
import { INQUIRY_DETAIL_TYPE, INQUIRY_DETAIL_TYPE_OPTIONS_MAP } from './constants';
import type { InquiryDetailType } from './types';
interface InquiryFormProps {
  type: InquiryType;
  isEdit?: boolean;
  orderId?: string;
  productNumber?: string;
  loading?: boolean;
  onSubmit?: (data: InquiryFormParams) => void;
  /** 기본 값 */
  fileUrlList?: string[];
  title?: string;
  contents?: string;
  privateYN?: boolean;
}
export const InquiryForm = t0 => {
  const $ = _c(94);
  const {
    type,
    isEdit,
    orderId,
    productNumber,
    loading,
    fileUrlList: defaultFileUrlList,
    title: defaultTitle,
    contents: defaultContents,
    privateYN: defaultPrivateYN,
    onSubmit
  } = t0;
  const {
    toast
  } = useToast();
  const {
    form
  } = useInquiryForm();
  const [inquiryDetailType, setInquiryDetailType] = useState();
  const {
    watch,
    setValue,
    register,
    trigger,
    reset,
    formState: t1
  } = form;
  const {
    errors
  } = t1;
  const fileUrlList = watch("fileUrlList");
  let t2;
  if ($[0] !== watch) {
    t2 = watch("privateYN");
    $[0] = watch;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const privateYN = t2;
  let t3;
  let t4;
  if ($[2] !== defaultContents || $[3] !== defaultFileUrlList || $[4] !== defaultPrivateYN || $[5] !== defaultTitle || $[6] !== reset) {
    t3 = () => {
      reset({
        fileUrlList: defaultFileUrlList ?? [],
        title: defaultTitle ?? "",
        contents: defaultContents ?? "",
        privateYN: Boolean(defaultPrivateYN)
      });
    };
    t4 = [defaultFileUrlList, defaultTitle, defaultContents, defaultPrivateYN, reset];
    $[2] = defaultContents;
    $[3] = defaultFileUrlList;
    $[4] = defaultPrivateYN;
    $[5] = defaultTitle;
    $[6] = reset;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== productNumber) {
    t5 = UnauthQueryOptions["get:/wello/v2/allows/home/gift-product/{productNumber}"]({
      path: {
        productNumber
      }
    });
    $[9] = productNumber;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = Boolean(productNumber) && type === INQUIRY_TYPE.GIFT_PRODUCT;
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = {
      ...t5,
      enabled: t6,
      select: _temp
    };
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    data: giftProductInfo,
    isLoading: isLoadingGiftProductInfo
  } = useQuery(t7);
  let t8;
  if ($[14] !== orderId) {
    t8 = AuthQueryOptions["get:/wello/v2/auth/home/gift/order/{orderId}"]({
      path: {
        orderId
      }
    });
    $[14] = orderId;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = Boolean(orderId) && type === INQUIRY_TYPE.DELIVERY;
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = {
      ...t8,
      enabled: t9,
      select: _temp2
    };
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    data: orderInfo,
    isLoading: isLoadingOrderInfo
  } = useQuery(t10);
  const isShowOrderIdArea = type === INQUIRY_TYPE.DELIVERY;
  const isShowProductArea = type === INQUIRY_TYPE.GIFT_PRODUCT || type === INQUIRY_TYPE.DELIVERY;
  const isShowFileUploadArea = type === INQUIRY_TYPE.ETC || type === INQUIRY_TYPE.DELIVERY;
  const isShowPrivateArea = type === INQUIRY_TYPE.GIFT_PRODUCT;
  const isFormLoading = loading || isLoadingGiftProductInfo || isLoadingOrderInfo;
  let t11;
  if ($[19] !== toast) {
    t11 = detailType => {
      switch (detailType) {
        case INQUIRY_DETAIL_TYPE.PRODUCT_INFO:
          {
            return true;
          }
        default:
          {
            toast({
              message: "\uD574\uB2F9 \uC720\uD615\uC740 \uCC44\uB110\uD1A1\uC73C\uB85C \uBB38\uC758\uD574\uC8FC\uC138\uC694.",
              type: "warning"
            });
            ChannelService.showMessenger();
            return false;
          }
      }
    };
    $[19] = toast;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const validationDetailType = t11;
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("flex h-full flex-1 flex-col", "mobile-tablet:pt-20");
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== isShowOrderIdArea || $[23] !== orderId) {
    t13 = isShowOrderIdArea && <div className={cn("mb-20 flex items-center gap-8 rounded-xsmall bg-Gray-50 px-20 py-12", "mobile-tablet:rounded-none mobile-tablet:px-20 mobile-tablet:py-16")}><p className="font-400 text-Gray-600 typo-body4">주문번호</p><p className="font-500 text-Gray-800 typo-body4">{orderId}</p></div>;
    $[22] = isShowOrderIdArea;
    $[23] = orderId;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== giftProductInfo?.donationAmount || $[26] !== giftProductInfo?.institutionName || $[27] !== giftProductInfo?.logoUrl || $[28] !== giftProductInfo?.name || $[29] !== giftProductInfo?.thumbnailUrl || $[30] !== isShowProductArea || $[31] !== orderInfo?.institutionName || $[32] !== orderInfo?.logoUrl || $[33] !== orderInfo?.name || $[34] !== orderInfo?.option || $[35] !== orderInfo?.thumbnailUrl || $[36] !== type) {
    t14 = isShowProductArea && <section className={cn("flex items-center gap-12 border-Gray-100 px-16", "mobile-tablet:border-b-8 mobile-tablet:px-20", type === INQUIRY_TYPE.GIFT_PRODUCT ? "pb-20" : "pb-35")}><div className="relative shrink-0"><Image src={type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.thumbnailUrl : orderInfo?.thumbnailUrl} alt={"\uC0C1\uD488 \uC774\uBBF8\uC9C0"} className="aspect-[1/1] w-full rounded-large border border-Gray-black/5 object-cover" width={80} height={80} /></div><div className="flex flex-col"><div className="flex items-center gap-2 pb-2"><div className="relative shrink-0 overflow-hidden rounded-pill border border-Gray-300"><Image src={type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.logoUrl : orderInfo?.logoUrl} alt={"\uC9C0\uC790\uCCB4 \uB85C\uACE0 \uC774\uBBF8\uC9C0"} className="aspect-[1/1] w-full rounded-large object-cover" width={20} height={20} /></div><p className="line-clamp-1 font-600 text-Gray-600 typo-caption1">{type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.institutionName : orderInfo?.institutionName}</p></div><div className="flex flex-1 flex-col"><p className="line-clamp-1 font-500 text-Gray-900 typo-body4">{type === INQUIRY_TYPE.GIFT_PRODUCT ? giftProductInfo?.name : orderInfo?.name}</p>{type === INQUIRY_TYPE.GIFT_PRODUCT ? <p className="line-clamp-1 pt-12 font-700 text-Gray-black typo-body2">{giftProductInfo?.donationAmount.toLocaleString()}원</p> : <p className="line-clamp-1 pt-6 font-400 text-Gray-600 typo-caption1">옵션: {orderInfo?.option ?? "-"}</p>}</div></div></section>;
    $[25] = giftProductInfo?.donationAmount;
    $[26] = giftProductInfo?.institutionName;
    $[27] = giftProductInfo?.logoUrl;
    $[28] = giftProductInfo?.name;
    $[29] = giftProductInfo?.thumbnailUrl;
    $[30] = isShowProductArea;
    $[31] = orderInfo?.institutionName;
    $[32] = orderInfo?.logoUrl;
    $[33] = orderInfo?.name;
    $[34] = orderInfo?.option;
    $[35] = orderInfo?.thumbnailUrl;
    $[36] = type;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("flex flex-col px-16 pb-20", "mobile-tablet:p-20");
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const t16 = isShowFileUploadArea && <section className="flex flex-col pb-32"><p className="font-400 text-Gray-800 typo-body4">첨부파일</p><ul className="flex w-full items-center gap-8 overflow-x-auto pb-16 pt-8 hide-scrollbar">{fileUrlList?.slice(0, FILE_UPLOAD_LIMIT_COUNT).map((fileUrl, index) => <li key={fileUrl}><FileUploadPlusBox type="HOME_INQUIRY" imageUrl={fileUrl} previewBlank={true} size={FILE_UPLOAD_BOX_SIZE} onDelete={() => {
          const newFileUrlList = fileUrlList?.filter((_, i) => i !== index);
          setValue("fileUrlList", newFileUrlList);
        }} /></li>)}{Array.from({
        length: FILE_UPLOAD_LIMIT_COUNT - fileUrlList.length
      }).map((__0, index_0) => <li key={index_0}><FileUploadPlusBox type="HOME_INQUIRY" size={FILE_UPLOAD_BOX_SIZE} limitByte={31457280} onUpload={fileUrl_0 => {
          if (!fileUrl_0) {
            toast({
              message: "\uCCA8\uBD80\uD30C\uC77C \uC5C5\uB85C\uB4DC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
              type: "fail"
            });
            return;
          }
          const newFileUrlList_0 = [...fileUrlList, fileUrl_0];
          setValue("fileUrlList", newFileUrlList_0);
        }} /></li>)}</ul><ul className="flex list-inside flex-col pl-20 font-400 text-Gray-600 typo-caption1"><li className="list-disc">파일 형식은 이미지 파일(jpg, jpeg, gif, png)로 제출 가능합니다.</li><li className="list-disc">파일 용량은 최대 30MB까지 가능합니다.</li></ul></section>;
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="font-400 text-Gray-800 typo-body4">문의 작성</p>;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== isShowPrivateArea || $[41] !== privateYN || $[42] !== setValue) {
    t18 = isShowPrivateArea && <Checkbox checked={privateYN} onCheckedChange={checked => {
      setValue("privateYN", checked);
    }}>비공개</Checkbox>;
    $[40] = isShowPrivateArea;
    $[41] = privateYN;
    $[42] = setValue;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <div className="flex items-center justify-between pb-8">{t17}{t18}</div>;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== inquiryDetailType || $[47] !== type || $[48] !== validationDetailType) {
    t20 = INQUIRY_DETAIL_TYPE_OPTIONS_MAP[type]?.length > 0 ? <DropdownMenu options={INQUIRY_DETAIL_TYPE_OPTIONS_MAP[type] ?? []} value={inquiryDetailType} onChange={value => {
      setInquiryDetailType(value);
      validationDetailType(value);
    }} placeholder={"\uBB38\uC758 \uC138\uBD80 \uC720\uD615\uC744 \uC120\uD0DD\uD574 \uC8FC\uC138\uC694."} className="mb-8" /> : null;
    $[46] = inquiryDetailType;
    $[47] = type;
    $[48] = validationDetailType;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = errors.title?.message;
  let t22;
  if ($[50] !== register) {
    t22 = register("title");
    $[50] = register;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = <Input placeholder={"\uBB38\uC758 \uC81C\uBAA9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} className="placeholder:font-400" dimensions="H48" {...t22} />;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t23) {
    t24 = <InputDecorator validationMessage={t21} className="mb-8">{t23}</InputDecorator>;
    $[54] = t21;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = errors.contents?.message;
  let t26;
  if ($[57] !== register) {
    t26 = register("contents");
    $[57] = register;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t26) {
    t27 = <textarea className="h-240 resize-none rounded-xsmall border border-Gray-300 px-16 py-12 font-400 text-Gray-900 outline-none typo-body3 placeholder:font-400 placeholder:text-Gray-550 placeholder:typo-body3" placeholder={"\uBB38\uC758 \uB0B4\uC6A9\uC744 \uC785\uB825\uD574 \uC8FC\uC138\uC694."} {...t26} />;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t25 || $[62] !== t27) {
    t28 = <InputDecorator validationMessage={t25}>{t27}</InputDecorator>;
    $[61] = t25;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t19 || $[65] !== t20 || $[66] !== t24 || $[67] !== t28) {
    t29 = <section className="flex flex-col">{t19}{t20}{t24}{t28}</section>;
    $[64] = t19;
    $[65] = t20;
    $[66] = t24;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t15 || $[70] !== t16 || $[71] !== t29) {
    t30 = <section className={t15}>{t16}{t29}</section>;
    $[69] = t15;
    $[70] = t16;
    $[71] = t29;
    $[72] = t30;
  } else {
    t30 = $[72];
  }
  let t31;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cn("w-full bg-Gray-white px-16 py-12", "mobile-tablet:sticky mobile-tablet:bottom-0 mobile-tablet:mt-auto mobile-tablet:px-20 mobile-tablet:py-12 mobile-tablet:shadow-lg");
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== form || $[75] !== inquiryDetailType || $[76] !== isFormLoading || $[77] !== onSubmit || $[78] !== trigger || $[79] !== validationDetailType) {
    t32 = async () => {
      if (isFormLoading) {
        return;
      }
      if (!validationDetailType(inquiryDetailType)) {
        return;
      }
      const valid = await trigger();
      if (!valid) {
        return;
      }
      onSubmit?.(form.getValues());
    };
    $[74] = form;
    $[75] = inquiryDetailType;
    $[76] = isFormLoading;
    $[77] = onSubmit;
    $[78] = trigger;
    $[79] = validationDetailType;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  const t33 = isEdit ? "\uC218\uC815" : "\uB4F1\uB85D";
  let t34;
  if ($[81] !== isFormLoading || $[82] !== t32 || $[83] !== t33) {
    t34 = <footer className={t31}><Button className="w-full" loading={isFormLoading} disabled={isFormLoading} onClick={t32}>{t33}</Button></footer>;
    $[81] = isFormLoading;
    $[82] = t32;
    $[83] = t33;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== isFormLoading) {
    t35 = isFormLoading ? <LoadingPageTemplate blur={true} fadeIn={true} /> : null;
    $[85] = isFormLoading;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== t12 || $[88] !== t13 || $[89] !== t14 || $[90] !== t30 || $[91] !== t34 || $[92] !== t35) {
    t36 = <section className={t12}>{t13}{t14}{t30}{t34}{t35}</section>;
    $[87] = t12;
    $[88] = t13;
    $[89] = t14;
    $[90] = t30;
    $[91] = t34;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  return t36;
};
const FILE_UPLOAD_LIMIT_COUNT = 5;
const FILE_UPLOAD_BOX_SIZE = 72;
function _temp(data) {
  return {
    logoUrl: data.context?.institutionLogoUrl,
    thumbnailUrl: data.context?.mainImageUrl,
    institutionName: data.context?.institutionName,
    name: data.context?.productName,
    donationAmount: data.context?.requiredDonationAmount ?? 0
  };
}
function _temp2(data_0) {
  const firstProduct = data_0.context?.products?.[0];
  return {
    logoUrl: data_0.context?.homeInstitutionLogoUrl,
    thumbnailUrl: firstProduct?.productMainImageUrl,
    institutionName: data_0.context?.homeInstitutionName,
    name: firstProduct?.productName,
    option: firstProduct?.optionItemList?.join(", ")
  };
}